import { FC, useCallback, useEffect, useMemo, useState } from 'react';

import { useAppDispatch, useAppSelector } from '../../../hooks/redux';
import Button from '../../common/button';
import { IPsyBlock } from '../new-questions/new-question-psy';
import { setActiveTool } from '../../../store/meetActiveTool.slice';
import { PsyToolsId } from '../../meet/tools-panel/types';

import { EmoView } from './emo-view';

export interface IDetectorEmotionPsy extends IPsyBlock {
  sendEmotionsOpen?: () => void;
  emotionsClientInbound?: { nonce: number; result: { category: string[]; emo: string[] } } | null;
  onEmotionsClientInboundConsumed?: () => void;
}

export const DetectorEmotionPsy: FC<IDetectorEmotionPsy> = ({
  result,
  setResult,
  onToolActivated,
  sendEmotionsOpen,
  emotionsClientInbound,
  onEmotionsClientInboundConsumed,
}) => {
  const psyTools = useAppSelector((state) => state.psyToolsSlice.tools);
  const dispatch = useAppDispatch();
  const [reactionId, setReactionId] = useState<string[]>([]);
  const [resultEmo, setResultEmo] = useState<string[]>([]);
  const [isClientChoosing, setClientChoosing] = useState(false);

  const settings = useMemo(
    () => psyTools.find((el) => el.name === 'emotions_detection')?.psy_tool_settings,
    [psyTools],
  );

  const createUpdateEmotions = useCallback(
    (cat: string[], emo: string[]) => {
      if (cat.length === 0) return;
      const data = settings?.filter((el) => cat.includes(el.name));
      const data1 = data?.map((el) => {
        return { ...el, value: el.value.filter((el) => emo.includes(el)) };
      });
      const data2 = data1
        ?.filter((el) => el.value.length > 0)
        .map((el) => {
          return { category: el.name, items: el.value };
        });
      const update = {
        date_time: new Date().toISOString(),
        comment: '',
        emotions: data2 || [],
      };
      if (result) {
        setResult({ ...result, emotions: [...result.emotions, update] });
      }
    },
    [result, setResult, settings],
  );

  useEffect(() => {
    if (!emotionsClientInbound) return;

    const { result: inboundResult } = emotionsClientInbound;
    createUpdateEmotions(inboundResult.category ?? [], inboundResult.emo ?? []);
    setResultEmo(inboundResult.emo ?? []);
    onEmotionsClientInboundConsumed?.();
  }, [emotionsClientInbound, createUpdateEmotions, onEmotionsClientInboundConsumed]);

  const screen0 = (
    <div className="text-base text-white p-[1rem]">
      Вы уверены, что хотите запросить у клиента его эмоциональное состояние в данный момент?
    </div>
  );
  const screenLast = (
    <div className="scroll-white overflow-y-auto mt-[1rem] mb-2.5">
      <div className=" flex gap-[15px] flex-wrap md:flex-col md:items-center w-full">
        {resultEmo.map((el, idx) => (
          <div
            className="w-full max-w-[240px] 2xl:max-w-[360px] bg-blackBaseOpacity20 rounded-[5px] py-[2px] px-0 lg:p-[5px_10px] text-center text-[16px] text-white font-semibold mx-auto"
            key={idx}
          >
            {el}
          </div>
        ))}
      </div>
    </div>
  );

  const screenClientChoosing = (
    <div className="h-full max-h-full flex flex-col scroll-white overflow-y-auto p-[1rem]">
      <div className="font-semiBold text-white pb-[2rem]">
        Клиент выбирает из списка эмоций. Выбранные эмоции отобразятся здесь.
      </div>
      <EmoView isPsiholog settings={settings} reactionId={reactionId} setReactionId={setReactionId} />
    </div>
  );

  return (
    <div className="sm:w-[30vw] lg:w-[15vw] md:min-w-[300px] h-full flex flex-col">
      <div className="flex mr-[3rem] ml-[1rem] mt-[1rem] font-comfortaa font-bold text-2xl text-white">
        Детектор эмоций
      </div>
      {!isClientChoosing ? screen0 : resultEmo.length === 0 ? screenClientChoosing : screenLast}
      {resultEmo.length === 0 && (
        <div className="mt-auto flex flex-row">
          {!isClientChoosing && (
            <Button
              className="!text-base xl:!text-base !self-center"
              onClick={() => {
                setClientChoosing(true);
                sendEmotionsOpen?.();
                onToolActivated?.('emotions_detection');
                dispatch(setActiveTool(PsyToolsId.EMOTIONS));
              }}
            >
              Запросить
            </Button>
          )}
          <div className="min-w-[48px] w-[48px]"></div>
        </div>
      )}
    </div>
  );
};
